/**
 * BOW-VALLEY — roster wave entries. GENERATED BY ASSEMBLY, WRITTEN BY REVIEW.
 *
 * Geography (streets, landmarks, coordinates, adjacency) ported from the source
 * roster; airspace derived by the calibrated classifier in
 * scripts/derive-airspace.mjs; every descriptive sentence written fresh from
 * aerial-media truth and fact-checked against its generation brief. See
 * docs/geo/VISION-v3.md for the fact contract these entries were written under.
 *
 * 6 entries. Edit by hand freely — assembly never overwrites a
 * file that has drifted from its fragments without --force.
 */
import type { Location } from "../types";

export const ROSTER_BOW_VALLEY: Location[] = [
{
    slug: "cottageclub",
    name: "CottageClub at Ghost Lake",
    region: "bow-valley",
    city: "Ghost Lake",
    province: "Alberta",
    coordinates: { lat: 51.2167, lng: -114.7333 },
    tier: 3,
    driveMinutesFromCalgary: 50,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 28,
      authorizationRequired: false,
      authorizationNote:
        "CottageClub at Ghost Lake lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 28 km from Springbank Airport (CYBW). No airspace authorisation is normally required. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "CottageClub's airspace is simpler than its geography: the community sits in uncontrolled Class G, 28 kilometres from Springbank Airport, so a flight that would need NAV CANADA authorisation over west Calgary needs none at all over Ghost Lake.",
      consequence:
        "Clients assume a lakefront shoot carries city-style clearance delays and pad the timeline for paperwork that does not exist here — or skip the aerial entirely and list a lake lot with no water in the frame.",
      resolution:
        "A boundary-line orbit of the lot with Ghost Lake and Ghost Dam in frame books on weather alone and is delivered in the standard turnaround.",
    },
    landmarks: ["Ghost Lake", "CottageClub Clubhouse", "Ghost Dam", "Highway 1A"],
    terrain:
      "A cottage community on the north shore of the Ghost Lake reservoir, where open rangeland drops to the water and the mountains stand up along the western horizon.",
    windNote:
      "Chinook belt, and the open water is honest about it: the reservoir sits in the path of west wind coming down the Bow corridor, and conditions over Ghost Lake often read stronger than the same day's forecast in shelter.",
    seasonalWindow:
      "Ice-off opens the water in spring, and the reservoir's level moves with Ghost Dam operations through the year, so early-summer and autumn frames can show noticeably different shorelines. Calm golden-hour evenings are the prize window.",
    bylawNote:
      "Public land around the reservoir falls under municipal park and pathway rules; the working default is a launch from the client's own lot with their consent.",
    subjectMix: ["resort-terrain", "luxury-residential"],
    mobilisationNote:
      "About 50 minutes west of the Calgary base on Highway 1A. Same-day is realistic, and a CottageClub booking pairs naturally with Cochrane or Ghost Lake work on the same run.",
    faqs: [
      {
        question: "Do drone flights at CottageClub on Ghost Lake need airspace authorisation?",
        answer:
          "Normally, no. CottageClub sits in uncontrolled Class G airspace, about 28 kilometres from Springbank Airport (CYBW) and outside the charted Calgary-area control zones, so no authorisation is usually required. The specific site and planned altitude are still confirmed against NAV Drone before every flight.",
      },
      {
        question: "What does aerial photography show at CottageClub that ground photos miss?",
        answer:
          "The water relationship. A lakefront lot's value is its position against Ghost Lake, and that geometry only reads from altitude — the lot line to the shoreline, the community around the CottageClub Clubhouse, and the reservoir stretching west toward the mountains. A boundary-line orbit puts all of it in one frame.",
      },
      {
        question: "Is CottageClub close enough to Calgary for a same-day shoot?",
        answer:
          "Yes. The drive is about 50 minutes west on Highway 1A, which keeps same-day scheduling realistic when the weather cooperates. Bookings here also pair efficiently with Cochrane or Ghost Lake addresses, so one mobilisation can serve two shoots inside the same light window.",
      },
    ],
    primaryKeywords: [
      "drone photography CottageClub at Ghost Lake",
      "aerial photography CottageClub at Ghost Lake",
      "CottageClub at Ghost Lake drone services",
    ],
    nearestLocations: ["ghost-lake", "cochrane", "exshaw", "lac-des-arcs"],
  },

  {
    slug: "homesteads",
    name: "Homesteads",
    region: "bow-valley",
    city: "Canmore",
    province: "Alberta",
    coordinates: { lat: 51.0698, lng: -115.3438 },
    tier: 4,
    driveMinutesFromCalgary: 75,
    airspace: {
      classification: "Class G uncontrolled, local aerodrome nearby",
      nearestAerodrome: "Canmore municipal heliport",
      distanceToAerodromeKm: 2,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 2 km from Canmore municipal heliport, Homesteads lies outside the charted Calgary-area control zones, and no authorisation is normally required. Canmore municipal heliport runs local circuit traffic nearby, so flights are planned with its procedures in mind. Before any flight, the exact site and altitude are checked against NAV Drone.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Homesteads sits in uncontrolled Class G airspace, but the number that actually shapes a flight plan is 2 — the kilometres to the Canmore municipal heliport, whose circuit traffic works the same slice of valley sky.",
      consequence:
        "An operator who reads the classification and nothing else treats this as open sky, then meets rotor traffic at exactly the altitude a property orbit wants — and the shoot that looked simple on paper gets cut short in the air.",
      resolution:
        "Altitudes and timing are set against the heliport's procedures before booking, so the full shot list — orbit, elevations, context frames — is flown once and delivered in the standard turnaround.",
    },
    landmarks: [
      "Canmore Nordic Centre",
      "Quarry Lake",
      "Rundle Range trailheads",
      "Three Sisters Pkwy",
      "Cougar Creek Dr",
    ],
    terrain:
      "Valley-floor and lower-bench residential ground in a steep-sided reach of the Bow Valley, with peaks rising steeply above the rooflines and the light window compressed at both ends of the day.",
    windNote:
      "Bow Valley funnelling stacked on the chinook belt: wind runs the axis of the valley and accelerates through it, so gusts over the neighbourhood often outrun the town forecast.",
    seasonalWindow:
      "The ranges take the evening sun early, so effective golden hour ends sooner than the almanac says. Snow holds on the surrounding slopes long after the valley floor is bare, which keeps white-backdrop frames alive well into spring.",
    bylawNote:
      "Town of Canmore rules govern takeoff and landing on municipal land, parks and pathways; launches default to private property with the client's consent.",
    subjectMix: ["luxury-residential", "resort-terrain"],
    mobilisationNote:
      "About 75 minutes from Calgary on the Trans-Canada. A comfortable day trip, and a Homesteads booking pairs well with South Canmore or Palliser addresses inside the same light window.",
    faqs: [
      {
        question: "Do drone flights in Homesteads need permission because of the Canmore heliport?",
        answer:
          "No authorisation is normally required — Homesteads sits in uncontrolled Class G airspace outside the Calgary-area control zones. The municipal heliport is about 2 kilometres away, though, so flights are planned with its circuit procedures in mind, and the exact site is confirmed against NAV Drone before every flight.",
      },
      {
        question: "Can an aerial shoot include the Canmore Nordic Centre or Quarry Lake as backdrop?",
        answer:
          "Yes, as context. The aircraft works from a lawful position over the client's property and composes those landmarks into the background — which is how mountain context sells a Homesteads address. Frames that would require flying over restricted ground are identified and removed from the shot list before booking.",
      },
    ],
    primaryKeywords: [
      "drone photography Homesteads",
      "aerial photography Homesteads",
      "Homesteads drone services",
    ],
    nearestLocations: ["south-canmore", "palliser-canmore", "lions-park"],
  },

  {
    slug: "lions-park",
    name: "Lions Park",
    region: "bow-valley",
    city: "Canmore",
    province: "Alberta",
    coordinates: { lat: 51.0912, lng: -115.3603 },
    tier: 4,
    driveMinutesFromCalgary: 80,
    airspace: {
      classification: "Class G uncontrolled, local aerodrome nearby",
      nearestAerodrome: "Canmore municipal heliport",
      distanceToAerodromeKm: 2,
      authorizationRequired: false,
      authorizationNote:
        "Lions Park lies outside the charted Calgary-area control zones in uncontrolled Class G airspace, about 2 km from Canmore municipal heliport. No airspace authorisation is normally required. Local circuit traffic at Canmore municipal heliport is close enough that flights are planned with the aerodrome's procedures in mind. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Along the Bow River pathway in Lions Park, autumn runs on two clocks — the valley floor stays green for weeks after the ranges above it take their first snow, so a single aerial frame here can hold both seasons at once.",
      consequence:
        "A listing shot on the wrong week misses that split entirely; from the ground nobody can see when the snowline is doing the work, and the client pays for a flat grey shoulder-season image instead.",
      resolution:
        "Shoots here are timed to the snowline and the light rather than the calendar — a golden-hour hold over the neighbourhood with fresh white on the ranges, delivered in the standard turnaround.",
    },
    landmarks: ["Lions Park", "Bow River pathway", "Canmore Main Street", "Riverside Dr"],
    terrain:
      "Flat valley-floor townsite blocks between the Bow River and Canmore Main Street, hemmed in by peaks that rise steeply above the streets on both sides of the valley.",
    windNote:
      "Chinook-belt air pushed through a mountain funnel: the valley channels west wind along its axis, and gusts over the townsite regularly beat the regional forecast.",
    seasonalWindow:
      "Snow reaches the peaks weeks before it reaches the streets and leaves the streets weeks before it leaves the slopes, so the shoulder seasons offer two-season frames. Midsummer brings the longest usable evenings.",
    bylawNote:
      "Takeoff and landing inside municipal parks — Lions Park included — falls under Town of Canmore rules, so launches stage from private property with the owner's consent.",
    subjectMix: ["luxury-residential", "tourism-hospitality"],
    mobilisationNote:
      "About 80 minutes from the Calgary base. Booked as a day trip, and usually paired with South Canmore or Teepee Town so one drive covers two shoots in the same light.",
    faqs: [
      {
        question: "Is it legal to fly a drone over the Lions Park area of Canmore?",
        answer:
          "Yes. Lions Park sits in uncontrolled Class G airspace and no authorisation is normally required. The Canmore municipal heliport is about 2 kilometres away, so flight plans respect its circuit traffic, and takeoff inside the municipal park itself is avoided — launches stage from private property, with the site confirmed on NAV Drone first.",
      },
      {
        question: "When is the best season for an aerial shoot in Lions Park?",
        answer:
          "The shoulder seasons earn their keep here. Snow sits on the ranges weeks before and after it touches the valley floor, so autumn and late-spring frames pair green ground along the Bow River pathway with white peaks. Midsummer offers the longest evenings; the light call is made from the snowline, not the calendar.",
      },
    ],
    primaryKeywords: [
      "drone photography Lions Park",
      "aerial photography Lions Park",
      "Lions Park drone services",
    ],
    nearestLocations: ["south-canmore", "teepee-town", "homesteads"],
  },

  {
    slug: "palliser-canmore",
    name: "Palliser",
    region: "bow-valley",
    city: "Canmore",
    province: "Alberta",
    coordinates: { lat: 51.0779, lng: -115.3684 },
    tier: 4,
    driveMinutesFromCalgary: 80,
    airspace: {
      classification: "Class G uncontrolled, local aerodrome nearby",
      nearestAerodrome: "Canmore municipal heliport",
      distanceToAerodromeKm: 2,
      authorizationRequired: false,
      authorizationNote:
        "Palliser sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and about 2 km from Canmore municipal heliport. Authorisation is not normally required here. Local circuit traffic at Canmore municipal heliport is close enough that flights are planned with the aerodrome's procedures in mind. Every flight is preceded by a NAV Drone check of the specific site and altitude.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "The Benchlands above Palliser face south into the winter sun, so snow burns off these slopes while the shaded wall across the valley stays white — from the air, one mid-winter frame can read as two different months.",
      consequence:
        "Time the shoot by the town forecast and that split works against you: a winter listing comes back half-brown, or a spring shoot comes back still buried, and neither looks like the property the client is selling.",
      resolution:
        "Aspect gets planned like weather — the shoot date is set to how the slope actually holds its snow, and the frames arrive in the standard turnaround.",
    },
    landmarks: [
      "Benchlands",
      "Trans-Canada Highway interchange",
      "Canmore Nordic Centre access",
      "Railway Ave",
      "Silvertip Trail",
    ],
    terrain:
      "Bench ground stepping up the north side of the Bow Valley from Railway Ave toward the Benchlands, with the valley floor and the Trans-Canada Highway interchange laid out below.",
    windNote:
      "The valley funnelling that runs the length of the Bow corridor, felt a little harder on the benches, which sit more exposed to west flow than the townsite grid below.",
    seasonalWindow:
      "South-facing aspect brings early green-up and long snow-free shoulders on the benches themselves, while the peaks opposite hold white backdrops well into June.",
    bylawNote:
      "Municipal park and pathway rules apply on Town of Canmore land; the working default is a launch from the client's own property with their consent.",
    subjectMix: ["luxury-residential", "resort-terrain"],
    mobilisationNote:
      "About 80 minutes out of Calgary on the Trans-Canada. Day-trip territory — and with South Canmore and Teepee Town minutes away, one mobilisation can serve several addresses.",
    faqs: [
      {
        question: "Does a Palliser drone shoot require NAV CANADA sign-off?",
        answer:
          "No — Palliser lies in uncontrolled Class G airspace outside the charted Calgary-area control zones, so no authorisation is normally needed. The Canmore municipal heliport sits about 2 kilometres away, close enough that flights are planned around its procedures, and every site is checked against NAV Drone before the flight.",
      },
      {
        question: "Why does slope aspect matter for aerial photography in Palliser?",
        answer:
          "The Benchlands face south, so their snow burns off while the opposite valley wall stays white. A winter listing shot a week late can look like early spring, and a spring shoot a week early can look buried. Dates here are set to how the slope actually holds snow, not the town forecast.",
      },
    ],
    primaryKeywords: [
      "drone photography Palliser",
      "aerial photography Palliser",
      "Palliser drone services",
    ],
    nearestLocations: ["south-canmore", "teepee-town", "homesteads"],
  },

  {
    slug: "south-canmore",
    name: "South Canmore",
    region: "bow-valley",
    city: "Canmore",
    province: "Alberta",
    coordinates: { lat: 51.0836, lng: -115.3572 },
    tier: 4,
    driveMinutesFromCalgary: 75,
    airspace: {
      classification: "Class G uncontrolled, local aerodrome nearby",
      nearestAerodrome: "Canmore municipal heliport",
      distanceToAerodromeKm: 2,
      authorizationRequired: false,
      authorizationNote:
        "In uncontrolled Class G airspace about 2 km from Canmore municipal heliport, South Canmore lies outside the charted Calgary-area control zones, and no authorisation is normally required. Canmore municipal heliport runs local circuit traffic nearby, so flights are planned with its procedures in mind. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "South Canmore's grid simply runs out of road at the river — Main St ends short of the Bow, and the crossing that anchors the frame is the Canmore Engine Bridge, a rail-era span that now carries foot traffic.",
      consequence:
        "That geography is the value and it is invisible from the ground: a buyer cannot tell that a 9th St address sits a few quiet blocks from the Main Street shops with the river, not through-traffic, at the end of the street.",
      resolution:
        "An approach run along the river line, then a rising reveal up the grid to the property, shows the quiet end of town and its walk to everything in one sequence — delivered in the standard turnaround.",
    },
    landmarks: [
      "Canmore Engine Bridge",
      "Policeman's Creek",
      "Riverside Park",
      "Main Street shops",
      "Old Canmore Rd",
    ],
    terrain:
      "The flat southern corner of the townsite grid, bounded by the Bow on one side and Policeman's Creek threading the other, with Riverside Park holding the river edge.",
    windNote:
      "Chinook belt, funnelled: the Bow Valley squeezes westerly flow along its axis, and the river-flat blocks feel it with little shelter.",
    seasonalWindow:
      "The river edge gives a short, strong autumn colour window; midsummer evenings run longest, and the surrounding peaks keep snow in the backdrop long after the streets are dry.",
    bylawNote:
      "Riverside Park and the rest of the municipal park network fall under Town of Canmore takeoff and landing rules; launches stage from private property with consent.",
    subjectMix: ["luxury-residential", "tourism-hospitality"],
    mobilisationNote:
      "About 75 minutes from Calgary on the Trans-Canada. Same-day shoots are practical, and Teepee Town or Lions Park addresses can share the mobilisation.",
    faqs: [
      {
        question: "Are drones allowed over South Canmore's riverside blocks?",
        answer:
          "Yes. South Canmore is uncontrolled Class G airspace and no authorisation is normally required. Two things still shape the plan: the municipal heliport about 2 kilometres away, whose circuit traffic is respected, and Town of Canmore park rules, which keep launches on private property. The site is confirmed against NAV Drone first.",
      },
      {
        question: "What does an aerial reveal about a South Canmore address that street photos cannot?",
        answer:
          "The geography of quiet. From altitude the frame shows the grid ending at the Bow, the Canmore Engine Bridge carrying foot traffic instead of trains, and Main Street shops a few blocks off — a property's distance from noise and its walk to everything, read in a single image.",
      },
    ],
    primaryKeywords: [
      "drone photography South Canmore",
      "aerial photography South Canmore",
      "South Canmore drone services",
    ],
    nearestLocations: ["teepee-town", "lions-park", "palliser-canmore", "homesteads"],
  },

  {
    slug: "teepee-town",
    name: "Teepee Town",
    region: "bow-valley",
    city: "Canmore",
    province: "Alberta",
    coordinates: { lat: 51.0897, lng: -115.3486 },
    tier: 4,
    driveMinutesFromCalgary: 75,
    airspace: {
      classification: "Class G uncontrolled, local aerodrome nearby",
      nearestAerodrome: "Canmore municipal heliport",
      distanceToAerodromeKm: 1,
      authorizationRequired: false,
      authorizationNote:
        "Teepee Town sits in uncontrolled Class G airspace, clear of the charted Calgary-area control zones and about 1 km from Canmore municipal heliport. Authorisation is not normally required here. Canmore municipal heliport runs local circuit traffic nearby, so flights are planned with its procedures in mind. The specific site and altitude are confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-09-01",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "From above, Teepee Town reads as two eras sharing one small grid — original small-lot cottages and new multi-storey infill on the same short blocks between Bow Valley Trail and the numbered avenues.",
      consequence:
        "That mix is exactly what a buyer or a builder needs to see — which lots have turned over, what a block is becoming — and none of it can be read from a car on 8 Ave.",
      resolution:
        "A straight-down series over the block, or an orbit setting the property against the Bow Valley Trail commercial strip, answers the question in one delivery — in the standard turnaround.",
    },
    landmarks: [
      "Canmore General Hospital",
      "Bow Valley Trail commercial strip",
      "Elevation Place",
      "8 Ave",
    ],
    terrain:
      "A compact valley-floor grid pressed between the Bow Valley Trail commercial strip and the older townsite, flat from block to block, with the valley walls doing all the vertical work in the frame.",
    windNote:
      "Valley-axis funnelling on top of the chinook belt; the flat grid offers little shelter when west wind runs, so calm-morning windows get used when they appear.",
    seasonalWindow:
      "The sun clears the ranges late and drops behind them early, so the practical golden hour is shorter than the almanac's — a constraint that gets planned around rather than discovered on the day.",
    bylawNote:
      "Town of Canmore governs takeoff and landing on municipal land and pathways; the default here is launching from the client's own lot or an agreed private site.",
    subjectMix: ["luxury-residential", "tourism-hospitality"],
    mobilisationNote:
      "About 75 minutes from the Calgary base. An easy day trip — and with South Canmore and Lions Park a few blocks away, one drive commonly serves more than one booking.",
    faqs: [
      {
        question: "Teepee Town is only a kilometre from the Canmore heliport — can you still fly?",
        answer:
          "Yes, and no authorisation is normally required: the airspace is uncontrolled Class G. What changes at roughly 1 kilometre is planning discipline — altitudes and timing are set with the heliport's circuit procedures in mind, and the specific site is confirmed against NAV Drone before every flight.",
      },
      {
        question: "Can you document a Teepee Town infill build as it progresses?",
        answer:
          "Yes. A repeated straight-down series over the lot, flown from the same positions each visit, gives a like-for-like record of an infill build from excavation to lock-up. On a small grid where blocks are changing quickly, that consistency is what makes the record useful — each pass delivered in the standard turnaround.",
      },
    ],
    primaryKeywords: [
      "drone photography Teepee Town",
      "aerial photography Teepee Town",
      "Teepee Town drone services",
    ],
    nearestLocations: ["south-canmore", "lions-park", "palliser-canmore"],
  }
];
